// PedidoMasivo.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePedidoMasivo } from '../components/usePedidoMasivo';
import VistaDetalleProveedor from '../components/PedidoDetalleProv';
import VistaTarjetasImagen from '../components/PedidosTarjeta';
import VistaCargaExcel from '../components/PedidosExcel';
import './Pedidos.css';

const PedidoMasivo = () => {
  const navigate = useNavigate();
  const hook = usePedidoMasivo();
  const [vistaActiva, setVistaActiva] = useState('excel');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ✅ Refresh manual: llama recargar() del hook
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      if (typeof hook.recargar === 'function') {
        await hook.recargar();
      } else {
        console.warn('[PedidoMasivo] hook.recargar no está disponible. ' +
          'Recargando página completa como fallback.');
        window.location.reload();
        return;
      }
      setUltimaActualizacion(new Date());
      setRefreshKey(k => k + 1); // remonta la vista para limpiar estado interno
    } catch (e) {
      console.error('[PedidoMasivo] ❌ Error al refrescar:', e);
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  }, [hook, isRefreshing]);

  // ✅ Auto-refresh al montar: datos siempre frescos al entrar
  useEffect(() => {
    let cancelado = false;
    const autoRefresh = async () => {
      if (typeof hook.recargar === 'function') {
        try {
          await hook.recargar();
          if (!cancelado) {
            setUltimaActualizacion(new Date());
            console.log('[PedidoMasivo] ✅ Auto-refresh inicial completado');
          }
        } catch (e) {
          if (!cancelado) console.error('[PedidoMasivo] Error en auto-refresh inicial:', e);
        }
      }
    };
    autoRefresh();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← solo al montar

  const vistas = {
    detalle: <VistaDetalleProveedor key={`detalle-${refreshKey}`} hook={hook} />,
    tarjetas: <VistaTarjetasImagen key={`tarjetas-${refreshKey}`} hook={hook} />,
    excel: <VistaCargaExcel key={`excel-${refreshKey}`} hook={hook} />,
  };

  return (
    <motion.div
      className="dashboard-container"
      style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* HEADER */}
      <header className="header-flex2" style={{ flexShrink: 0 }}>
        <div className="title-section">
          <button
            className="new-order-btn"
            onClick={() => navigate('/pedidos')}
            style={{ marginRight: '1rem' }}
          >
            <ArrowLeft size={18} />
            <span style={{ marginLeft: '8px' }}>Volver</span>
          </button>
        </div>

        <div className="title-section">
          <h1 className="dashboard-title">
            Pedido Masivo {hook.isAsesor && "(su cartera)"}
          </h1>
          <div className="title-underline"></div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* ✅ BOTÓN DE REFRESH */}
          <button
            className="new-order-btn"
            onClick={handleRefresh}
            disabled={isRefreshing || hook.isLoadingCatalog}
            title={
              ultimaActualizacion
                ? `Última actualización: ${ultimaActualizacion.toLocaleTimeString()}`
                : 'Refrescar datos desde el servidor'
            }
            style={{
              background: isRefreshing ? 'rgba(59, 130, 246, 0.2)' : 'rgba(0, 186, 82, 0.2)',
              border: '1px solid rgba(0, 186, 82, 0.4)',
              cursor: isRefreshing || hook.isLoadingCatalog ? 'not-allowed' : 'pointer',
              opacity: isRefreshing || hook.isLoadingCatalog ? 0.7 : 1,
            }}
          >
            <RefreshCw
              size={18}
              className={isRefreshing ? 'spin-anim' : ''}
            />
            <span style={{ marginLeft: '8px' }}>
              {isRefreshing
                ? 'Actualizando...'
                : hook.isLoadingCatalog
                  ? 'Cargando...'
                  : 'Refrescar'}
            </span>
          </button>

          {/* Tabs de vistas */}
          <button
            className="new-order-btn"
            onClick={() => setVistaActiva('excel')}
            style={{
              background: vistaActiva === 'excel'
                ? 'var(--text-theme, #00ba52)'
                : 'rgba(59, 130, 246, 0.2)',
              border: vistaActiva === 'excel' ? 'none' : '1px solid rgba(59, 130, 246, 0.4)',
            }}
          >
            <FileSpreadsheet size={18} />
            <span style={{ marginLeft: '8px' }}>Excel</span>
          </button>
          <button
            className="new-order-btn"
            onClick={() => setVistaActiva('detalle')}
            style={{
              background: vistaActiva === 'detalle'
                ? 'var(--text-theme, #00ba52)'
                : 'rgba(59, 130, 246, 0.2)',
              border: vistaActiva === 'detalle' ? 'none' : '1px solid rgba(59, 130, 246, 0.4)',
            }}
          >
            Detalle
          </button>
          <button
            className="new-order-btn"
            onClick={() => setVistaActiva('tarjetas')}
            style={{
              background: vistaActiva === 'tarjetas'
                ? 'var(--text-theme, #00ba52)'
                : 'rgba(59, 130, 246, 0.2)',
              border: vistaActiva === 'tarjetas' ? 'none' : '1px solid rgba(59, 130, 246, 0.4)',
            }}
          >
            Tarjetas
          </button>
        </div>
      </header>

      {/* CUERPO - VISTA ACTIVA */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {vistas[vistaActiva]}
      </div>
    </motion.div>
  );
};

export default PedidoMasivo;